import { z } from 'zod';
import {
  entityBase,
  id,
  organizationId,
  timestamp,
  paginationQuery,
  paginatedResult,
} from './common.js';

export const rateeType = z.enum(['professional', 'office']);
export type RateeType = z.infer<typeof rateeType>;

export const review = entityBase.extend({
  bookingId: id,
  raterUserId: id,
  rateeType,
  rateeId: z.string().min(1),
  stars: z.number().int().min(1).max(5),
  body: z.string().nullable(),
});
export type Review = z.infer<typeof review>;

export const createReviewInput = z.object({
  organizationId,
  bookingId: id,
  raterUserId: id,
  rateeType,
  rateeId: z.string().min(1),
  stars: z.number().int().min(1).max(5),
  body: z.string().trim().max(2000).nullish(),
});
export type CreateReviewInput = z.infer<typeof createReviewInput>;

export const actorType = z.enum(['professional', 'office']);
export type ActorType = z.infer<typeof actorType>;

export const strike = entityBase.extend({
  actorType,
  actorId: z.string().min(1),
  bookingId: id.nullable(),
  shiftId: id.nullable(),
  reason: z.string().min(1),
  weight: z.number().nonnegative(),
});
export type Strike = z.infer<typeof strike>;

export const reputationSubjectType = z.enum(['professional', 'office']);
export type ReputationSubjectType = z.infer<typeof reputationSubjectType>;

export const reputationScore = entityBase.extend({
  subjectType: reputationSubjectType,
  subjectId: z.string().min(1),
  avgStars: z.number(),
  reviewCount: z.number().int().nonnegative(),
  strikeWeight: z.number(),
  score: z.number(),
});
export type ReputationScore = z.infer<typeof reputationScore>;

export const scheduleHoldStatus = z.enum(['active', 'released']);
export type ScheduleHoldStatus = z.infer<typeof scheduleHoldStatus>;

export const scheduleHold = entityBase.extend({
  professionalId: id,
  bookingId: id,
  shiftId: id,
  startsAt: timestamp,
  endsAt: timestamp,
  status: scheduleHoldStatus,
});
export type ScheduleHold = z.infer<typeof scheduleHold>;

export const cancelBookingInput = z.object({
  reason: z.string().min(1).max(500),
});
export type CancelBookingInput = z.infer<typeof cancelBookingInput>;

/** Review management exposes only genuine booking participants and the author's own history. */
export const reviewWorkspace = z.enum(['professional', 'office']);
export const reviewListQuery = paginationQuery.extend({
  workspace: reviewWorkspace.default('professional'),
  search: z.string().trim().max(100).optional(),
});
export type ReviewListQuery = z.infer<typeof reviewListQuery>;
export const reviewOpportunity = z.object({
  bookingId: id,
  shiftId: id,
  counterpartName: z.string(),
  locationName: z.string(),
  startsAt: timestamp,
  endsAt: timestamp,
  timezone: z.string(),
});
export type ReviewOpportunity = z.infer<typeof reviewOpportunity>;
export const authoredReview = review.extend({
  counterpartName: z.string(),
  locationName: z.string(),
  startsAt: timestamp,
  endsAt: timestamp,
  timezone: z.string(),
});
export const reviewOpportunityPage = paginatedResult(reviewOpportunity);
export const authoredReviewPage = paginatedResult(authoredReview);
export const bookingReviewInput = z
  .object({
    workspace: reviewWorkspace,
    stars: z.number().int().min(1).max(5),
    body: z.string().trim().max(2000).optional(),
  })
  .strict();
export type BookingReviewInput = z.infer<typeof bookingReviewInput>;
